'use client'

import ArrowForward from '@/app/_components/Icon/ArrowForward'
import Step1 from '@/app/_components/OnBoarding/Steps/Step1'
import Step2 from '@/app/_components/OnBoarding/Steps/Step2'
import { cn } from '@/app/utils'
import { useI18n } from '@/locales/client'
import { Transition } from '@headlessui/react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Fragment, useEffect, useState } from 'react'

type OnbordingProps = {
  noShowInDays?: number
}

const STORAGE_KEY = 'onBoardingNoShow'

export default function OnboardingPopup({ noShowInDays = 1 }: OnbordingProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [type, setType] = useState<'creator' | 'user' | null>(null)

  useEffect(() => {
    const now = Date.now()
    const expireTime = parseInt(localStorage.getItem(STORAGE_KEY) || '', 10)

    if (!expireTime || isNaN(expireTime) || now > expireTime) {
      setIsOpen(true)
    }
  }, [])

  const closePopupToday = () => {
    const expire = Date.now() + noShowInDays * 24 * 60 * 60 * 1000
    localStorage.setItem(STORAGE_KEY, expire.toString())
    setIsOpen(false)
  }

  if (!isOpen) return null

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
      <DialogPrimitive.Portal forceMount>
        <Transition.Root show={isOpen}>
          <Transition.Child
            as={Fragment}
            enter={'ease-out duration-300'}
            enterFrom={'opacity-0'}
            enterTo={'opacity-100'}
            leave={'ease-in duration-300'}
            leaveFrom={'opacity-100'}
            leaveTo={'opacity-0'}>
            <DialogPrimitive.Overlay
              forceMount
              className={'absolute inset-0 z-[10000001] bg-[#000000D9]'}
            />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95">
            <DialogPrimitive.Content
              forceMount
              id={'on-boarding-dialog'}
              onInteractOutside={(e) => e.preventDefault()}
              className={cn(
                'fixed z-[10000001] flex flex-col gap-32 lg:gap-12',
                'items-center',
                'aspect-[63/100] h-full w-360 pb-100 pt-44 lg:h-500 lg:w-502',
                'max-lg:w-360',
                'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'bg-[#00000000] text-[#FAFAFA]',
              )}>
              <Header
                type={type}
                onClose={() => setIsOpen(false)}
                onHide={closePopupToday}
                onTypeChange={() => setType(null)}
              />
              <Step1 type={type} setType={setType} />
              <Step2 type={type} setType={setType} />
              <Footer
                type={type}
                onClose={() => setIsOpen(false)}
                onHide={closePopupToday}
              />
            </DialogPrimitive.Content>
          </Transition.Child>
        </Transition.Root>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

type HeaderFooterProps = {
  type: 'creator' | 'user' | null
  onHide: () => void
  onClose: () => void
  onTypeChange?: () => void
}

function Header({ type, onClose, onHide, onTypeChange }: HeaderFooterProps) {
  return (
    <div className="c-h8 flex w-full items-center justify-between p-16 lg:hidden">
      {type !== null ? (
        <ArrowForward
          className={'size-24 scale-x-[-1] cursor-pointer fill-icon-inverse'}
          onClick={onTypeChange}
        />
      ) : (
        <NoShowTodayButton onClick={onHide} />
      )}
      <SkipButton onClick={onClose} />
    </div>
  )
}

function Footer({ onClose, onHide }: HeaderFooterProps) {
  return (
    <>
      {/* <div className={'flex gap-8 lg:hidden'}>
        <Dot active={type === null} />
        <Dot active={type !== null} />
      </div> */}
      <div className="c-b3 hidden w-full items-center justify-between py-16 lg:flex">
        <NoShowTodayButton onClick={onHide} />
        <SkipButton onClick={onClose} />
      </div>
    </>
  )
}

function NoShowTodayButton({ onClick }: { onClick: () => void }) {
  const t = useI18n()
  return (
    <div
      aria-hidden
      onClick={onClick}
      className="flex cursor-pointer items-center gap-5">
      {t('onboarding.noShowToday')}
    </div>
  )
}

function SkipButton({ onClick }: { onClick: () => void }) {
  return (
    <DialogPrimitive.Close
      onClick={onClick}
      className="c-h7 flex items-center justify-center outline-none">
      Skip
    </DialogPrimitive.Close>
  )
}

// function Dot({ active }: { active: boolean }) {
//   return (
//     <div
//       className={cn('size-6 rounded-full bg-white/50', {
//         'bg-butterscotch': active,
//       })}
//     />
//   )
// }
